'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { TrendingUp, Clock, X, Search } from 'lucide-react';
import { useT } from '@/components/i18n-provider';

const RECENT_SEARCHES_KEY = 'discover-recent-searches';
const MAX_RECENT = 5;

function getRecentSearches(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_SEARCHES_KEY);
    if (!raw) return [];
    return JSON.parse(raw).slice(0, MAX_RECENT);
  } catch { return []; }
}

export function addRecentSearch(query: string) {
  if (!query.trim()) return;
  try {
    const recent = getRecentSearches().filter(q => q !== query.trim());
    recent.unshift(query.trim());
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(recent.slice(0, MAX_RECENT)));
  } catch { /* ignore */ }
}

function removeRecentSearch(query: string) {
  try {
    const recent = getRecentSearches().filter(q => q !== query);
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(recent));
  } catch { /* ignore */ }
}

interface SearchSuggestionsProps {
  visible: boolean;
  onSelect: (query: string) => void;
}

export function SearchSuggestions({ visible, onSelect }: SearchSuggestionsProps) {
  const t = useT();
  const [recent, setRecent] = useState<string[]>([]);
  const [trending, setTrending] = useState<string[]>([]);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    setRecent(getRecentSearches());
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    const ctrl = new AbortController();
    fetch('/api/tags?limit=8&sort=popular', { signal: ctrl.signal })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.tags) {
          setTrending(data.tags.map((t: any) => t.name).slice(0, 8));
        }
      })
      .catch(() => {});
    return () => ctrl.abort();
  }, [visible]);

  if (!mounted || !visible) return null;

  const hasContent = recent.length > 0 || trending.length > 0;
  if (!hasContent) return null;

  return (
    <div className="absolute top-full left-0 right-0 mt-1 z-30 bg-popover border border-border rounded-xl shadow-lg overflow-hidden">
      {recent.length > 0 && (
        <div className="px-3 pt-2.5 pb-1">
          <div className="flex items-center gap-1.5 mb-1.5">
            <Clock className="h-3 w-3 text-muted-foreground" />
            <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">{t('marketplace.recent')}</span>
          </div>
          {recent.map(q => (
            <div key={q} className="flex items-center gap-2 group">
              <button
                onClick={() => onSelect(q)}
                className="flex-1 text-left px-2 py-1.5 text-sm hover:bg-accent rounded-md transition-colors truncate"
              >
                {q}
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  removeRecentSearch(q);
                  setRecent(prev => prev.filter(x => x !== q));
                }}
                className="p-1 rounded-md opacity-0 group-hover:opacity-100 hover:bg-accent transition-all shrink-0"
              >
                <X className="h-3 w-3 text-muted-foreground" />
              </button>
            </div>
          ))}
        </div>
      )}

      {trending.length > 0 && (
        <div className="px-3 pt-2.5 pb-2.5 border-t border-border/50">
          <div className="flex items-center gap-1.5 mb-1.5">
            <TrendingUp className="h-3 w-3 text-muted-foreground" />
            <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">{t('nav.trending')}</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {trending.map(tag => (
              <button
                key={tag}
                onClick={() => onSelect(tag)}
                className="inline-flex items-center gap-0.5 px-2.5 py-1 rounded-full text-xs font-medium bg-accent/60 hover:bg-accent text-foreground transition-colors"
              >
                <span className="text-muted-foreground">#</span>{tag}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
